import { Component, Input, OnChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { IGlobalRatingPeriod } from '@core/interfaces/iratingperiod';
import { RatingPeriodService } from '@core/services/rating-period.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@core/services/toast.service';
import { MatTableDataSource } from '@angular/material/table';
import { formatDate } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { Modal } from '@core/enums/modal';
import { TableColumnType } from '@core/enums/table';
import { SchoolYear } from '@core/enums/school-year';
import { TranslocoService } from '@jsverse/transloco';
import { translateTermName } from '@core/services/helper.service';

@Component({
    selector: 'drdp-add-global-rating-period',
    templateUrl: './add-global-rating-period.component.html',
    styleUrls: ['./add-global-rating-period.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddGlobalRatingPeriodComponent implements OnChanges, OnDestroy {
  private subscription = new Subscription();

  @Input() currentYear: any;
  @Input() currentPeriods: IGlobalRatingPeriod[] = [];
  validForm$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  forms: FormGroup[] = [];
  globalFall: FormGroup | any;
  globalWinter: FormGroup | any;
  globalSpring: FormGroup | any;
  globalSummer: FormGroup | any;
  tableData?: any;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  noSubmit: boolean = true;
  agenciesApplied: boolean = false;
  formTableColumns: any[] = [];

  constructor(
    private ratingPeriodService: RatingPeriodService,
    public toast: ToastService,
    public modal: MatDialog,
    private fb: FormBuilder,
    private translocoService: TranslocoService
  ) {}

  buildTableColumns(): void {
    if (this.formTableColumns.length) return;
    this.formTableColumns = [
      { columnDef: 'schoolYearName', header: this.translocoService.translate('configuration.common.schoolYear'), type: TableColumnType.text },
      { columnDef: 'periodName', header: this.translocoService.translate('configuration.globalRatingPeriods.columns.term'), type: TableColumnType.text },
      {
        columnDef: 'defaultStartDate',
        header: this.translocoService.translate('configuration.globalRatingPeriods.columns.defaultStartDate'),
        type: TableColumnType.datepicker,
      },
      {
        columnDef: 'defaultEndDate',
        header: this.translocoService.translate('configuration.globalRatingPeriods.columns.defaultEndDate'),
        type: TableColumnType.datepicker,
      },
      {
        columnDef: 'minStartDate',
        header: this.translocoService.translate('configuration.globalRatingPeriods.columns.minStartDate'),
        type: TableColumnType.datepicker,
      },
      { columnDef: 'maxEndDate', header: this.translocoService.translate('configuration.globalRatingPeriods.columns.maxEndDate'), type: TableColumnType.datepicker },
    ];
  }

  ngOnChanges(): void {
    this.buildTableColumns();
    if (this.currentYear) {
      this.initializeForms();
    }
  }

  initializeForms(): void {
    const year = this.currentYear.schoolYearName;
    this.forms= [];
    this.forms.push((this.globalSummer = this.generateFormGroup('Summer')));
    this.forms.push((this.globalFall = this.generateFormGroup('Fall')));
    this.forms.push((this.globalWinter = this.generateFormGroup('Winter')));
    this.forms.push((this.globalSpring = this.generateFormGroup('Spring')));


    this.globalSummer
      .get('globalRatingPeriodName')
      .setValue('Summer ' + year.substring(0, 4));
    this.globalFall
      .get('globalRatingPeriodName')
      .setValue('Fall ' + year.substring(0, 4));
    this.globalWinter
      .get('globalRatingPeriodName')
      .setValue('Winter ' + year);
    this.globalSpring
      .get('globalRatingPeriodName')
      .setValue('Spring ' + year.substring(0, 2) + year.substring(5, 7));

    this.globalSummer.get('periodName').setValue('Summer');
    this.globalFall.get('periodName').setValue('Fall');
    this.globalWinter.get('periodName').setValue('Winter');
    this.globalSpring.get('periodName').setValue('Spring');
    this.generateTableData();
  }

  generateFormGroup(season: string): FormGroup {
    return this.fb.group({
      schoolYearName: [season == 'Summer' ? this.currentYear.schoolYearName : ''],
      schoolYearId: [this.currentYear.id],
      globalRatingPeriodName: [season],
      periodName: [null],
      minStartDate: [null, Validators.required],
      maxEndDate: [null, Validators.required],
      defaultStartDate: [null, Validators.required],
      defaultEndDate: [null, Validators.required],
      isSelect: [true]
    });
  }

  generateTableData(): void {
    if (this.currentPeriods.length) {
      this.noSubmit = false;
      this.formTableColumns.forEach((x) => {
        x.type = TableColumnType.text;
      });

      for (let per in this.currentPeriods) {
        switch (this.currentPeriods[per].periodName) {
          case 'Summer': {
            this.populateFormValues(this.globalSummer, this.currentPeriods[per]);
            break;
          }
          case 'Fall': {
            this.populateFormValues(this.globalFall, this.currentPeriods[per]);
            break;
          }
          case 'Winter': {
            this.populateFormValues(
              this.globalWinter,
              this.currentPeriods[per]
            );
            break;
          }
          case 'Spring': {
            this.populateFormValues(
              this.globalSpring,
              this.currentPeriods[per]
            );
            break;
          }

          default: {
            break;
          }
        }
      }
    }

    this.dataSource.data = this.tableRows();
    this.tableData = this.tableRows();

    this.subscription.add(
      this.ratingPeriodService
        .globalRatingsApplied(this.currentYear.id)
        .subscribe((res) => {
          if (res) {
            this.agenciesApplied = res;
          }
        })
    );
  }

  private tableRows(): any[] {
    return this.forms.map((form) => ({
      ...form.value,
      periodName: translateTermName(form.value.periodName, (key) => this.translocoService.translate(key)),
    }));
  }

  populateFormValues(form: FormGroup, period: IGlobalRatingPeriod): void {
    form
      .get('defaultStartDate')
      ?.setValue(formatDate(period.defaultStartDate, 'M/d/yyyy', 'en-US'));
    form
      .get('defaultEndDate')
      ?.setValue(formatDate(period.defaultEndDate, 'M/d/yyyy', 'en-US'));
    form
      .get('minStartDate')
      ?.setValue(formatDate(period.minStartDate, 'M/d/yyyy', 'en-US'));
    form
      .get('maxEndDate')
      ?.setValue(formatDate(period.maxEndDate, 'M/d/yyyy', 'en-US'));
    form.get('periodName')?.setValue(period.periodName);
  }

  dateChange(change: any): void {
    let i = change.index;
    let property = change.property;
    let val = change.value;

    this.forms[i].get(property)?.setValue(formatDate(val, 'M/d/yyyy', 'en-US'));

    for (let form in this.forms) {
      if (!this.forms[form].valid) {
        this.validForm$.next(false);
        return;
      }
      this.validForm$.next(true);
    }
  }

  saveGlobalRatingPeriods() {
    let mappedPeriods = this.forms.map((form: any) => {
      let x = form.value;
      return <IGlobalRatingPeriod>{
        id: 0,
        schoolYearId: x.schoolYearId,
        globalRatingPeriodName: x.globalRatingPeriodName,
        minStartDate: x.minStartDate,
        maxEndDate: x.maxEndDate,
        defaultStartDate: x.defaultStartDate,
        defaultEndDate: x.defaultEndDate,
      };
    });

    this.subscription.add(
      this.ratingPeriodService
        .addGlobalRatingPeriod(mappedPeriods)
        .subscribe((success) => {
          if (success) {
            this.formTableColumns.forEach((x) => {
              x.type = TableColumnType.text;
            });
            this.currentPeriods = mappedPeriods;
            this.noSubmit = false;
            this.dataSource.data = this.tableRows();
            this.tableData = this.tableRows();
            this.toast.success(this.translocoService.translate('configuration.globalRatingPeriods.savedToast'));
          } else {
            this.toast.error(this.translocoService.translate('configuration.globalRatingPeriods.saveFailedToast'));
          }
        })
    );
  }

  applyGlobalRatingPeriods(): void {
    const payload = {
      schoolYearId: this.currentYear.id,
      schoolYearName: this.currentYear.schoolYearName,
    };

    this.subscription.add(
      this.ratingPeriodService
        .applyGlobalRatingPeriodsExisting(payload)
        .subscribe((success) => {
          if (success) {
            this.agenciesApplied = true;
            this.toast.success(
              this.translocoService.translate('configuration.globalRatingPeriods.appliedToast')
            );
          }
        })
    );
  }

  isValidDateOrder(form: FormGroup): boolean {
    return (
      new Date(form.get('minStartDate')?.value) <
        new Date(form.get('maxEndDate')?.value) &&
      new Date(form.get('defaultStartDate')?.value) <
        new Date(form.get('defaultEndDate')?.value)
    );
  }

  confirmSave(): void {
    if (!this.validForm$.getValue()) {
      this.toast.error(this.translocoService.translate('configuration.common.formIncompleteToast'));
      return;
    }

    for (var form in this.forms) {
      if (!this.isValidDateOrder(this.forms[form])) {
        this.toast.error(this.translocoService.translate('configuration.globalRatingPeriods.invalidDateOrderToast'));
        return;
      }
    }

    let event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('configuration.globalRatingPeriods.confirmSaveTitle'),
      message: this.translocoService.translate('configuration.globalRatingPeriods.confirmSaveMessage'),
      name: 'Global Rating Periods',
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.Confirm,
    };

    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        this.saveGlobalRatingPeriods();
      }
    });
  }

  confirmSubmit(): void {
    let event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: this.translocoService.translate('configuration.globalRatingPeriods.confirmSubmitTitle'),
      message: this.translocoService.translate('configuration.globalRatingPeriods.confirmSubmitMessage'),
      name: 'Global Rating Periods',
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.Confirm,
      showCancel: true,
    };

    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        this.applyGlobalRatingPeriods();
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
